import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, SelectOptionTypes, SelectTypes } from '@dotcom/crown-ui';
import { Font, FontSelectedOption } from '../font-drawer/font-drawer-types';
import styles from './font-family-control.module.scss';

export type FontFamilyControlProps = {
  fonts: Font[];
  initialFontFamily: string;
  onChange: (option: FontSelectedOption) => void;
  /** Label shown above the dropdown. Defaults to the translated 'fontDrawer.fontStyle' key. */
  label?: string;
  testId?: string;
  /** When true the dropdown list opens upward instead of downward (use on mobile where space below is limited). */
  dropUp?: boolean;
};

/**
 * Standalone font-family selector. Manages its own selected-font preview
 * state and notifies the parent via `onChange` for canvas updates.
 *
 * Uses Crown UI's `isCustomSelect` + `variant="font"` so each option is
 * rendered with its own `fontFamily` style (font preview out of the box).
 *
 * Has no canvas or context dependencies — drop it anywhere, including
 * Storybook.
 */
export const FontFamilyControl = ({
  fonts,
  initialFontFamily,
  onChange,
  label,
  testId = 'font-family-control',
  dropUp = false,
}: FontFamilyControlProps): React.ReactElement => {
  const { t } = useTranslation();
  const [selectedFont, setSelectedFont] = useState(initialFontFamily);

  const options: (SelectOptionTypes & { id: number })[] = fonts.map((font) => ({
    label: font.text,
    value: font.text,
    font: font.family,
    id: font.id,
  }));

  const handleChange = (option: SelectOptionTypes) => {
    setSelectedFont(option.label);
    onChange(option as FontSelectedOption);
  };

  return (
    <div className={`${styles['font-family-control']}${dropUp ? ` ${styles['font-family-control--drop-up']}` : ''}`}>
      <Dropdown
        label={label ?? t('fontDrawer.fontStyle')}
        options={options}
        value={selectedFont}
        onChange={(option) => handleChange(option as SelectOptionTypes)}
        variant={SelectTypes.Font}
        isCustomSelect
        testId={testId}
      />
    </div>
  );
};
